import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SEO from '../components/SEO'

export default function LeadsDashboardPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [leads, setLeads] = useState([])
  const [stats, setStats] = useState({ total: 0, classes: { player_bloccato: 0, rogue_invisibile: 0, eroe_in_addestramento: 0 } })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Filtri tabella
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClass, setSelectedClass] = useState('all')

  useEffect(() => {
    // Verifica se c'è un token già salvato nel localStorage
    const savedToken = localStorage.getItem('nerdcoach_admin_token')
    if (savedToken === 'nerdcoach-session-token-2026-secret') {
      setIsAuthenticated(true)
      fetchLeads(savedToken)
    }
  }, [])

  const fetchLeads = async (token) => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch('/api/admin/leads', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setLeads(data.leads || [])
        setStats(data.stats || { total: 0, classes: { player_bloccato: 0, rogue_invisibile: 0, eroe_in_addestramento: 0 } })
      } else {
        const errData = await response.json()
        setError(errData.error || 'Sessione scaduta o non valida')
        // Rimuove il token errato
        localStorage.removeItem('nerdcoach_admin_token')
        setIsAuthenticated(false)
      }
    } catch (err) {
      console.error(err)
      setError('Errore di collegamento con il server delle gilde.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('nerdcoach_admin_token', data.token)
        setIsAuthenticated(true)
        fetchLeads(data.token)
      } else {
        const errData = await response.json()
        setError(errData.error || 'Password amministrativa errata')
      }
    } catch (err) {
      console.error(err)
      setError('Errore di autenticazione con il server.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('nerdcoach_admin_token')
    setIsAuthenticated(false)
    setLeads([])
    setPassword('')
  }

  const handleDeleteLead = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questo lead? Verrà rimosso permanentemente.')) return
    
    const token = localStorage.getItem('nerdcoach_admin_token')
    try {
      const response = await fetch(`/api/admin/leads/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Aggiorna l'elenco locale
        setLeads(leads.filter(l => l.id !== id))
        // Ricalcola al volo statistiche locali rapide
        const deletedLead = leads.find(l => l.id === id)
        if (deletedLead) {
          const updatedStats = { ...stats }
          updatedStats.total--
          if (updatedStats.classes[deletedLead.profile] !== undefined) {
            updatedStats.classes[deletedLead.profile]--
          }
          setStats(updatedStats)
        }
      } else {
        alert('Impossibile rimuovere il lead dal database.')
      }
    } catch (err) {
      console.error(err)
      alert('Errore durante la chiamata di rimozione.')
    }
  }

  // Esportazione in formato CSV
  const handleExportCSV = () => {
    if (leads.length === 0) return
    
    const headers = ['ID', 'Email', 'Classe', 'Data Iscrizione', 'Scelte Gioco']
    const rows = leads.map(l => [
      l.id,
      l.email,
      l.profile,
      new Date(l.timestamp).toLocaleString('it-IT'),
      l.choices.join(' - ')
    ])

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.map(val => `"${val}"`).join(','))].join('\n')
    
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `nerdcoach_leads_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Filtra Leads in base a barra di ricerca e selettore classe
  const filteredLeads = leads.filter(l => {
    const matchesSearch = l.email.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesClass = selectedClass === 'all' || l.profile === selectedClass
    return matchesSearch && matchesClass
  })

  // Mappa classi
  const classNames = {
    player_bloccato: { label: 'Player Bloccato', color: '#8b5cf6' },
    rogue_invisibile: { label: 'Rogue Invisibile', color: '#00d4ff' },
    eroe_in_addestramento: { label: 'Eroe in Addestramento', color: '#facc15' }
  }

  return (
    <div className="min-h-screen pt-24 pb-24 px-4 bg-[#050510] relative overflow-hidden">
      <SEO 
        title="Dashboard Leads | Nerd Coach Admin" 
        description="Pannello di controllo amministrativo protetto per tracciare e gestire i lead del Libro-Game."
      />

      {/* Retroilluminazione cyberpunk */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(139,92,246,0.015) 40px, rgba(139,92,246,0.015) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(139,92,246,0.015) 40px, rgba(139,92,246,0.015) 41px)' }} />

      <div className="max-w-6xl mx-auto relative z-10">
        <AnimatePresence mode="wait">
          
          {/* SCHERMATA LOGIN ADMIN */}
          {!isAuthenticated ? (
            <motion.div
              key="login"
              className="max-w-md mx-auto py-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
            >
              <div 
                className="manga-panel rounded-2xl p-6 md:p-8 relative overflow-hidden"
                style={{
                  border: '2px solid rgba(139,92,246,0.3)',
                  background: 'linear-gradient(135deg, rgba(15,15,36,0.95), rgba(8,8,24,0.98))',
                  boxShadow: '0 8px 32px rgba(139,92,246,0.2)'
                }}
              >
                {/* Decals */}
                <div className="absolute top-2 left-2 w-3.5 h-3.5 border-l border-t border-nerd-cyan/30" />
                <div className="absolute top-2 right-2 w-3.5 h-3.5 border-r border-t border-nerd-cyan/30" />
                <div className="absolute bottom-2 left-2 w-3.5 h-3.5 border-l border-b border-nerd-cyan/30" />
                <div className="absolute bottom-2 right-2 w-3.5 h-3.5 border-r border-b border-nerd-cyan/30" />

                <div className="text-center mb-6">
                  <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.25em] font-bold block mb-1">
                    System Administration
                  </span>
                  <h2 className="font-display text-2xl font-bold text-nerd-white">
                    Accesso Registro Leads
                  </h2>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[9px] text-nerd-gray uppercase tracking-wider font-display font-semibold">
                      Chiave Amministrativa (Password):
                    </label>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Inserisci la chiave reale..."
                      className="w-full px-3 py-2.5 rounded-lg text-xs bg-nerd-bg border border-nerd-purple/20 text-nerd-white placeholder-nerd-gray/45 focus:outline-none focus:border-nerd-cyan transition-all"
                    />
                  </div>

                  {error && (
                    <div className="text-nerd-red text-[10px] bg-nerd-red/10 border border-nerd-red/35 p-2.5 rounded-lg text-center font-display uppercase tracking-wider">
                      ⚠ {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 nerd-btn-primary font-display text-[10px] font-bold uppercase tracking-wider transition-all duration-300"
                    style={{ boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)' }}
                  >
                    {loading ? 'Validazione...' : 'Sblocca Dashboard 🗝️'}
                  </button>
                </form>
              </div>
            </motion.div>
          ) : (
            
            // DASHBOARD PRINCIPALE
            <motion.div
              key="dashboard"
              className="space-y-8"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header Dashboard */}
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b border-nerd-purple/15 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-nerd-cyan animate-pulse" />
                    <span className="text-[10px] text-nerd-cyan font-display uppercase tracking-widest font-bold">Pannello di Controllo Leads</span>
                  </div>
                  <h1 className="font-display text-3xl font-bold text-nerd-white mt-1">
                    Checkpoint Leads — <span className="text-gradient">Nerd Coach</span>
                  </h1>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleExportCSV}
                    disabled={leads.length === 0}
                    className="px-4 py-2 rounded bg-nerd-dark border border-nerd-cyan/30 text-nerd-cyan hover:bg-nerd-cyan/10 hover:border-nerd-cyan text-[9px] font-display uppercase tracking-wider transition-all"
                  >
                    Esporta CSV 📥
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 rounded bg-nerd-dark border border-nerd-red/30 text-nerd-red hover:bg-nerd-red/10 hover:border-nerd-red text-[9px] font-display uppercase tracking-wider transition-all"
                  >
                    Esci ✕
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-nerd-red text-xs bg-nerd-red/10 border border-nerd-red/30 p-3 rounded-lg font-display uppercase tracking-wider text-center">
                  {error}
                </div>
              )}

              {/* STATS CARDS GRID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Leads Totali */}
                <div className="manga-panel rounded-xl p-5 bg-nerd-dark/45 border-nerd-purple/20">
                  <span className="text-[8px] text-nerd-gray uppercase tracking-widest font-display block">Total Active Leads</span>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="font-display text-3xl font-bold text-nerd-white">{stats.total}</span>
                    <span className="text-[10px] text-nerd-purple font-display uppercase tracking-wider">Membri</span>
                  </div>
                  <div className="h-1 w-full bg-nerd-bg rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-nerd-purple" style={{ width: '100%' }} />
                  </div>
                </div>

                {/* Player Bloccati */}
                <div className="manga-panel rounded-xl p-5 bg-nerd-dark/45 border-nerd-purple/20">
                  <span className="text-[8px] text-nerd-gray uppercase tracking-widest font-display block">Player Bloccati</span>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="font-display text-3xl font-bold text-[#8b5cf6]">{stats.classes.player_bloccato}</span>
                    <span className="text-[9px] text-nerd-gray/60 font-display">
                      ({stats.total > 0 ? Math.round((stats.classes.player_bloccato / stats.total) * 100) : 0}%)
                    </span>
                  </div>
                  <div className="h-1 w-full bg-nerd-bg rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-[#8b5cf6]" style={{ width: `${stats.total > 0 ? (stats.classes.player_bloccato / stats.total) * 100 : 0}%` }} />
                  </div>
                </div>

                {/* Rogue Invisibili */}
                <div className="manga-panel rounded-xl p-5 bg-nerd-dark/45 border-nerd-purple/20">
                  <span className="text-[8px] text-nerd-gray uppercase tracking-widest font-display block">Rogue Invisibili</span>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="font-display text-3xl font-bold text-[#00d4ff]">{stats.classes.rogue_invisibile}</span>
                    <span className="text-[9px] text-nerd-gray/60 font-display">
                      ({stats.total > 0 ? Math.round((stats.classes.rogue_invisibile / stats.total) * 100) : 0}%)
                    </span>
                  </div>
                  <div className="h-1 w-full bg-nerd-bg rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-[#00d4ff]" style={{ width: `${stats.total > 0 ? (stats.classes.rogue_invisibile / stats.total) * 100 : 0}%` }} />
                  </div>
                </div>

                {/* Eroi in Addestramento */}
                <div className="manga-panel rounded-xl p-5 bg-nerd-dark/45 border-nerd-purple/20">
                  <span className="text-[8px] text-nerd-gray uppercase tracking-widest font-display block">Eroi in Addestramento</span>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="font-display text-3xl font-bold text-[#facc15]">{stats.classes.eroe_in_addestramento}</span>
                    <span className="text-[9px] text-nerd-gray/60 font-display">
                      ({stats.total > 0 ? Math.round((stats.classes.eroe_in_addestramento / stats.total) * 100) : 0}%)
                    </span>
                  </div>
                  <div className="h-1 w-full bg-nerd-bg rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-[#facc15]" style={{ width: `${stats.total > 0 ? (stats.classes.eroe_in_addestramento / stats.total) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>

              {/* FILTRI DI RICERCA */}
              <div className="flex flex-col sm:flex-row gap-4 items-center bg-nerd-dark/30 border border-nerd-purple/10 rounded-xl p-4">
                {/* Search */}
                <div className="flex-1 w-full relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Filtra email leads..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg text-xs bg-nerd-bg border border-nerd-purple/15 text-nerd-white placeholder-nerd-gray/40 focus:outline-none focus:border-nerd-cyan"
                  />
                  <div className="absolute left-3 top-2.5 text-nerd-gray/50">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </div>
                </div>

                {/* Selettore classe */}
                <div className="w-full sm:w-56">
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-xs bg-nerd-bg border border-nerd-purple/15 text-nerd-white focus:outline-none focus:border-nerd-cyan"
                  >
                    <option value="all">Tutte le Classi</option>
                    <option value="player_bloccato">Player Bloccato</option>
                    <option value="rogue_invisibile">Rogue Invisibile</option>
                    <option value="eroe_in_addestramento">Eroe in Addestramento</option>
                  </select>
                </div>
              </div>

              {/* TABELLA LEADS */}
              <div 
                className="manga-panel rounded-xl overflow-hidden bg-nerd-dark/20 relative"
                style={{ border: '1px solid rgba(139,92,246,0.15)' }}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-nerd-purple/10 border-b border-nerd-purple/20 text-nerd-cyan uppercase font-display text-[9px] tracking-wider">
                        <th className="p-4">Email</th>
                        <th className="p-4">Classe Rilevata</th>
                        <th className="p-4">Data Iscrizione</th>
                        <th className="p-4">Codice ID</th>
                        <th className="p-4 text-center">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="p-12 text-center text-nerd-gray font-display uppercase tracking-widest text-[10px] animate-pulse">
                            Caricamento dati dal checkpoint...
                          </td>
                        </tr>
                      ) : filteredLeads.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="p-12 text-center text-nerd-gray/60 font-display uppercase tracking-wider text-[9px]">
                            Nessun lead registrato corrispondente ai criteri di filtro.
                          </td>
                        </tr>
                      ) : (
                        filteredLeads.map((lead) => {
                          const cls = classNames[lead.profile] || { label: lead.profile, color: '#9ca3af' }
                          return (
                            <motion.tr 
                              key={lead.id}
                              className="border-b border-nerd-purple/5 hover:bg-nerd-purple/5 transition-colors font-body"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              <td className="p-4 font-semibold text-nerd-white select-all">{lead.email}</td>
                              <td className="p-4">
                                <span 
                                  className="px-2 py-0.5 rounded text-[8px] font-display font-semibold uppercase tracking-wider"
                                  style={{ color: cls.color, background: `${cls.color}15`, border: `1px solid ${cls.color}30` }}
                                >
                                  {cls.label}
                                </span>
                              </td>
                              <td className="p-4 text-nerd-gray">
                                {new Date(lead.timestamp).toLocaleString('it-IT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="p-4 font-mono text-[9px] text-nerd-gray/60">{lead.id}</td>
                              <td className="p-4 text-center">
                                <button
                                  onClick={() => handleDeleteLead(lead.id)}
                                  className="p-1.5 rounded bg-nerd-red/10 border border-nerd-red/20 text-nerd-red hover:bg-nerd-red/25 hover:border-nerd-red transition-all inline-flex items-center justify-center"
                                  title="Elimina Lead"
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                </button>
                              </td>
                            </motion.tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer tabella */}
                <div className="p-4 bg-nerd-purple/5 border-t border-nerd-purple/10 flex justify-between items-center text-[9px] text-nerd-gray uppercase tracking-wider font-display">
                  <span>Mostrati: {filteredLeads.length} su {leads.length}</span>
                  <span className="text-nerd-cyan">Checkpoint Real-Time Leads</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
